import {Interpolation, SerializedStyles, Theme} from '@emotion/react';
import {css, Typography, useTheme} from '@mui/material';
import {DateUtils, DlMuiPopper, GetStylesFunction, LoadingSpinner, useDebounce, useOnMount, useSeparators} from '@wallaby-ui/core';
import {cloneDeep, isNumber} from 'lodash';
import * as Plotly from 'plotly.js';
import {PlotHoverEvent, PlotMouseEvent, PlotData as PlotlyPlotData} from 'plotly.js';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import createPlotlyComponent from 'react-plotly.js/factory';

import {useFormatters} from './LightFormatter';

const Plot = createPlotlyComponent(Plotly);

// emulate plotly auto ticks
function _tickRoundUp(value: number, roundingSet: number[]) {
  let closest = 1;
  let left = 0;
  roundingSet.forEach((right) => {
    const midpoint = (left + right) / 2;
    if (value >= midpoint) closest = right;
    left = right;
  });
  return closest;
}
const BASE10_ROUNDING_SET = [2, 5, 10];
type AutoTicksProps = {
  min: number;
  max: number;
  nTicks: number;
};
export function getAutoTicks(props: AutoTicksProps): {
  tick0: number;
  dtick: number;
} {
  const {min} = props;
  let {max, nTicks} = props;
  if (max === min) {
    max += 1;
    nTicks = Math.ceil(nTicks / 2); // NOTE: plotly always shows negative values here
  }
  const tick0 = min;
  const roughDTick = (max - min || 1) / nTicks;
  const floorBase10 = 10 ** Math.floor(Math.log10(roughDTick));
  const dtick = floorBase10 * _tickRoundUp(roughDTick / floorBase10, BASE10_ROUNDING_SET);
  return {tick0, dtick};
}
export function getAutoTicksArray(props: AutoTicksProps): {tickvals: number[]} {
  const {max} = props;
  const {tick0, dtick} = getAutoTicks(props);
  const tickvals: number[] = [];
  for (let tick = tick0; tick <= max; tick += dtick) tickvals.push(tick);
  return {tickvals};
}
export type ChartColor = {
  id?: string | number;
  color: string;
  fillColor?: string;
  label: string;
  size?: number;
  hidden?: boolean;
  // Keep the series plotted but drop it from the (downloaded-image) legend. The on-screen chart
  // hides Plotly's own legend, so this only affects the image export — used to keep a report's
  // downloaded chart legend in sync with the on-screen, HAI-state-filtered custom legend.
  hideFromLegend?: boolean;
};
function getDefaultHoverText(a: string | number, b: string | number, color: ChartColor, colors: ChartColor[]): string {
  return colors.length > 1 ? `${a}<br><b>${color.label}</b>: ${b}` : `${a}<br>${b}`;
}

// Word-wrap a label into <br>-separated lines so a long single line doesn't overflow (and get
// clipped on) the downloaded chart image. Used for the image title and the legend labels — a long
// pathogen name (e.g. "Glutamátdehydrogenasa (antigen Clostridium difficile)") is wrapped onto
// multiple lines rather than truncated with an ellipsis, so the full name stays readable.
function wrapTextByChars(text: string, maxChars: number): string {
  if (!text || maxChars <= 0 || text.length <= maxChars) return text;
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (line && line.length + 1 + word.length > maxChars) {
      lines.push(line);
      line = word;
    } else {
      line = line ? `${line} ${word}` : word;
    }
  }
  if (line) lines.push(line);
  return lines.join('<br>');
}

// data
type BaseTrace = {
  name?: string;
  hoverinfo?: PlotlyPlotData['hoverinfo'];
  hovertext?: string[];
  cliponaxis?: boolean;
  customdata?: (string | number)[]; // NOTE: full (un-truncated) axis labels, used for y-tick tooltips
  showlegend?: boolean; // NOTE: per-trace legend visibility (mainly relevant for the image export)
};

// bar chart
export type LightBarChartTrace = {
  type: 'bar';
  orientation: 'h';
  offsetgroup?: number;
  marker: {color: string};
  x: number[];
  y: string[];
  visible?: true | false | 'legendonly';
} & BaseTrace;
export function getBarChartItems<T>(
  data: T[],
  colors: ChartColor[],
  getXY: (
    value: T,
    index: number
  ) => {
    x: LightBarChartTrace['x'][0];
    y: LightBarChartTrace['y'][0];
    tooltip?: string;
    yTooltip?: string;
    xTooltip?: string;
  }
): LightBarChartTrace[] {
  const reversedData = [...data].reverse(); // NOTE: first item at top
  return colors.map((color, i) => {
    const mappedData = reversedData.map((v) => getXY(v, i));
    return {
      type: 'bar',
      orientation: 'h',
      marker: {color: color.color},
      offsetgroup: 0,
      x: mappedData.map((v) => v.x),
      y: mappedData.map((v, i) => `${v.y}${'\u200B'.repeat(i)}`),
      customdata: mappedData.map((v) => v.yTooltip ?? v.y), // NOTE: full label for the y-tick tooltip
      hovertext: mappedData.map((v) => v.tooltip ?? getDefaultHoverText(v.yTooltip ?? v.y, v.xTooltip ?? v.x, color, colors)),
      hoverinfo: 'text',
      name: color.label,
      visible: !color.hidden,
      showlegend: color.hideFromLegend ? false : undefined,
    } as LightBarChartTrace;
  });
}
export function getBarChartHeight(rowCount: number) {
  return 102 + 14 * Math.max(0, rowCount - 1); // NOTE: minimum is Math.max(102, 14n + 46)
}
// vertical bar chart
type LightVerticalBarChartTrace = {
  type: 'bar';
  orientation: 'v';
  offsetgroup?: number;
  marker: {color: string};
  x: string[];
  y: number[];
  visible?: true | false | 'legendonly';
};
export function getVerticalBarChartItems<T>(
  data: T[],
  colors: ChartColor[],
  getXY: (
    value: T,
    index: number
  ) => {
    x: LightVerticalBarChartTrace['x'][0];
    y: LightVerticalBarChartTrace['y'][0];
    tooltip?: string;
    yTooltip?: string;
    xTooltip?: string;
  }
): LightVerticalBarChartTrace[] {
  const reversedData = [...data]; // NOTE: first item at left
  return colors.map((color, i) => {
    const mappedData = reversedData.map((v) => getXY(v, i));
    return {
      type: 'bar',
      orientation: 'v',
      marker: {color: color.color},
      offsetgroup: 0,
      x: mappedData.map((v) => v.x),
      y: mappedData.map((v) => v.y),
      hovertext: mappedData.map((v) => v.tooltip ?? getDefaultHoverText(v.xTooltip ?? v.x, v.yTooltip ?? v.y, color, colors)),
      hoverinfo: 'text',
      name: color.label,
      visible: !color.hidden,
    } as LightVerticalBarChartTrace;
  });
}

// line chart
export type LightLineChartTrace = {
  type: 'scatter';
  mode: 'markers' | 'lines' | 'lines+markers';
  orientation?: 'h' | 'v';
  marker: {
    size?: number;
    color: string;
  };
  x: (string | number)[];
  y: number[];
  visible?: true | false | 'legendonly';
} & BaseTrace;
export function getLineChartItems<T>(
  data: T[],
  colors: ChartColor[],
  getXY: (
    value: T,
    index: number
  ) => {
    x: LightLineChartTrace['x'][0];
    y: LightLineChartTrace['y'][0];
    tooltip?: string;
    yTooltip?: string;
    xTooltip?: string;
  }
): LightLineChartTrace[] {
  const reversedData = [...data].reverse(); // NOTE: first item at top
  return colors.map((color, i) => {
    const mappedData = reversedData.map((v) => getXY(v, i));
    return {
      type: 'scatter',
      mode: 'lines+markers',
      marker: {color: color.color, size: color.size},
      x: mappedData.map((v) => v.x),
      y: mappedData.map((v) => v.y),
      hovertext: mappedData.map((v) => v.tooltip ?? getDefaultHoverText(v.xTooltip ?? v.x, v.yTooltip ?? v.y, color, colors)),
      hoverinfo: 'text',
      name: color.label,
      visible: !color.hidden,
      showlegend: color.hideFromLegend ? false : undefined,
    } as LightLineChartTrace;
  });
}

export type LightAreaChartTrace = {
  type: 'scatter';
  mode: 'lines' | 'lines+markers' | 'none';
  fill: 'tozeroy' | 'tonexty' | 'toself' | 'tonextx';
  stackgroup?: string; // Pro stacked area charts
  line: {
    color: string;
    width?: number;
  };
  fillcolor?: string;
  x: (string | number)[];
  y: number[];
  visible?: true | false | 'legendonly';
} & BaseTrace;
export function getAreaChartItems<T>(
  data: T[],
  colors: ChartColor[],
  getXY: (
    value: T,
    index: number
  ) => {
    x: LightAreaChartTrace['x'][0];
    y: LightAreaChartTrace['y'][0];
    tooltip?: string;
  },
  isStacked: boolean = false
): LightAreaChartTrace[] {
  return colors.map((color, i) => {
    const mappedData = data.map((v) => getXY(v, i));

    let fill: LightAreaChartTrace['fill'] = 'tozeroy';
    if (isStacked && !color.hidden) {
      const hasVisibleBefore = colors.slice(0, i).some((c) => !c.hidden);
      fill = hasVisibleBefore ? 'tonexty' : 'tozeroy';
    }
    return {
      type: 'scatter',
      mode: 'lines+markers',
      fill,
      stackgroup: isStacked ? 'one' : undefined, // Všechny série ve stejné stackgroup
      line: {
        color: color.color,
        width: 2,
      },
      fillcolor: color.fillColor ?? color.color,
      x: mappedData.map((v) => v.x),
      y: mappedData.map((v) => v.y),
      hovertext: mappedData.map((v) => v.tooltip ?? getDefaultHoverText(v.x, v.y, color, colors)),
      hoverinfo: 'text',
      name: color.label,
      visible: !color.hidden,
    } as LightAreaChartTrace;
  });
}

export type LightChartTrace = LightBarChartTrace | LightVerticalBarChartTrace | LightLineChartTrace | LightAreaChartTrace;

// layout
type LayoutTicksSubType =
  | {
      tickmode?: 'auto';
      nticks?: number; // NOTE: this only sets the maximum, plotly may use less ticks
    }
  | {
      tickmode: 'linear';
      tick0: string | number; // NOTE: can be number or date string
      dtick: number;
    }
  | {
      tickmode: 'array';
      tickvals: (string | number)[];
      ticktext?: string[];
    };
type LayoutAxis = LayoutTicksSubType & {
  // axis
  automargin?: boolean;
  fixedrange?: boolean; // fixedrange == !zoomable
  showgrid?: boolean;
  gridcolor?: string;
  // ticks
  type?: '-' | 'linear' | 'log' | 'date' | 'category' | 'multicategory';
  rangemode?: 'normal' | 'tozero' | 'nonnegative';
  range?: (string | number)[];
  tickformat?: string; // NOTE: e.g. ".1"
  showticklabels?: boolean;
  tickformatstops?: {
    dtickrange: [string | number | null, string | number | null];
    value: string;
  }[];
  tickangle?: number;
  showaxeslabels?: boolean;
  autorange?: boolean;
};
// https://plotly.com/javascript/reference/
export type LightChartTitle = {
  text: string;
  font?: {
    family?: string;
    size?: number;
    color?: string;
  };
  x?: number;
  xanchor?: 'auto' | 'left' | 'center' | 'right';
  xref?: 'container' | 'paper';
  y?: number;
  yanchor?: 'auto' | 'top' | 'middle' | 'bottom';
  yref?: 'container' | 'paper';
};
export type LightChartLayout = {
  margin?: {
    t?: number;
    b?: number;
    l?: number;
    r?: number;
    pad?: number;
  };
  xaxis: LayoutAxis;
  yaxis: LayoutAxis;
  title?: LightChartTitle;
  showlegend?: boolean;
  barmode?: 'stack' | 'group' | 'overlay' | 'relative';
  width?: number;
  height?: number;
};
export type LightChartConfig = {
  displaylogo?: boolean;
  showTips?: boolean;
  modeBarButtonsToAdd?: {
    name: string;
    title: string;
    icon: Plotly.Icon;
    click: (gd: Plotly.PlotlyHTMLElement, event: MouseEvent) => void;
  }[];
  modeBarButtonsToRemove?: Plotly.ModeBarDefaultButtons[];
  showAxisRangeEntryBoxes?: boolean;
};

export type LightChartImageDownloadProps = {
  title?: LightChartTitle;
  showlegend?: boolean;
  fileName?: string;
};
type Props = {
  data: LightChartTrace[];
  layout: LightChartLayout;
  config?: LightChartConfig;
  emptyMessage?: string;
  imageDownload?: LightChartImageDownloadProps;
  plotStyle?: React.CSSProperties;
  emptyWrapperStyle?: React.CSSProperties;
  onClick?: (onClickData: PlotMouseEvent) => void;
  onClickYTick?: (data: string) => void;
  onHover?: (event: PlotHoverEvent) => void;
  onUnhover?: (event: PlotMouseEvent) => void;
  isLoading?: boolean;
  chartStyles?: SerializedStyles | Interpolation<Theme>;
  noDataComponent?: React.ReactNode;
  originalDataLength?: number;
};
// Tooltip for truncated y-axis tick labels, controlled imperatively (see usage in LightChart).
type YAxisTooltipHandle = {
  show: (anchorEl: Element, text: string) => void;
  hide: () => void;
};
const YAxisTooltip = React.forwardRef<YAxisTooltipHandle>((_props, ref) => {
  const [tooltip, setTooltip] = useState<{anchorEl: Element; text: string} | null>(null);
  React.useImperativeHandle(
    ref,
    () => ({
      show: (anchorEl, text) => setTooltip({anchorEl, text}),
      hide: () => setTooltip(null),
    }),
    []
  );
  if (!tooltip) return null;
  return (
    <DlMuiPopper hasTooltip open anchorEl={tooltip.anchorEl} placement={'top'}>
      <Typography variant={'popper'}>{tooltip.text}</Typography>
    </DlMuiPopper>
  );
});
YAxisTooltip.displayName = 'YAxisTooltip';

// Plotly only clamps its auto-margins for plot areas larger than `minreducedwidth`/`minreducedheight`
// (both default to 64px). Below that the clamp is skipped, the plot-area length goes negative and
// `ax.setScale` throws "Something went wrong with axis scaling" (set_convert.js). We feed Plotly an
// explicit width/height floored above that threshold so a chart squeezed to ~0 (e.g. a split panel
// dragged shut) renders clipped instead of crashing.
const MIN_PLOT_PX = 80;
// Floor a measured dimension to MIN_PLOT_PX. A non-finite measurement (a hidden container reports
// `width: auto`, so getWrapperSize's `+'auto'.slice(0, -2)` is NaN) falls back to the floor instead
// of handing Plotly NaN — note `Math.max(NaN, 80)` is NaN, and `?? ` would not catch NaN either.
const floorPlotSize = (value: number) => (Number.isFinite(value) ? Math.max(value, MIN_PLOT_PX) : MIN_PLOT_PX);

export const LightChart: React.FC<Props> = (props) => {
  const {
    data,
    layout: _layout,
    config: _config = {},
    emptyMessage,
    imageDownload,
    plotStyle,
    emptyWrapperStyle,
    onClick,
    onClickYTick,
    onHover,
    onUnhover,
    isLoading,
    chartStyles,
    noDataComponent,
    originalDataLength,
    ...restProps
  } = props;
  const {t} = useFormatters();
  // layout
  const [_chartNonce, _setChartNonce] = useState(0);
  const [isAnyDataVisible, setIsAnyDataVisible] = useState<boolean>(data.length > 0);
  const rerender = useCallback(() => {
    _setChartNonce((v) => v + 1);
  }, []);
  // tooltip for truncated y-axis tick labels (reuses the same styled popper as table cells).
  // Controlled imperatively via a ref so showing/hiding it does NOT re-render this component —
  // a re-render would hand <Plot> fresh cloned props and force Plotly to redraw the tick nodes,
  // detaching the very element the tooltip is anchored to.
  const yTickTooltipRef = useRef<YAxisTooltipHandle>(null);
  const handleYTickMouseOver = useCallback((event: Event) => {
    const target = event.currentTarget as Element | null;
    const text = target?.getAttribute('data-yticktooltip');
    if (target && text) yTickTooltipRef.current?.show(target, text);
  }, []);
  const handleYTickMouseOut = useCallback(() => yTickTooltipRef.current?.hide(), []);
  const chartWrapperRefValue = useRef(null as HTMLElement | null);
  // Last real measurement. Used as the fallback when the wrapper node is momentarily detached
  // (e.g. the render right after `isLoading` flips false, before the ref callback re-attaches):
  // returning the stale-but-real size there keeps Plotly's explicit width/height correct instead
  // of collapsing to 0 → MIN_PLOT_PX. Without it a chart that goes through any loading cycle
  // (applying a filter) renders stuck at 80px wide, because `_onResize`'s prevWrapperSize guard
  // still holds the pre-loading width and so skips the corrective re-render.
  const lastMeasuredSize = useRef({width: 0, height: 230});
  const getWrapperSize = useCallback(() => {
    if (!chartWrapperRefValue.current) return lastMeasuredSize.current;
    const computedStyles = window.getComputedStyle(chartWrapperRefValue.current);
    const size = {
      width: +computedStyles.width.slice(0, -2),
      height: +computedStyles.height.slice(0, -2),
    };
    // Only cache genuine measurements — a hidden container reports `width: auto` (→ NaN).
    if (Number.isFinite(size.width) && size.width > 0) lastMeasuredSize.current = size;
    return size;
  }, []);
  const wrapperSize = getWrapperSize(); // eslint-disable-line
  const TOOLBAR_MARGIN_TOP = 24;
  const separators = useSeparators();

  const getDefaultRange = () => {
    const isAnyNegative = data.some((value) => {
      const {x, y} = value;
      let isNegative = false;
      if (Array.isArray(x) && !isNegative) {
        isNegative = x.some((xval) => typeof xval === 'number' && xval < 0);
      }
      if (Array.isArray(y) && !isNegative) {
        isNegative = y.some((yval) => typeof yval === 'number' && yval < 0);
      }
      return isNegative;
    });

    if (isAnyNegative && !isAnyDataVisible) {
      return {
        range: [0, 5],
      };
    }

    if (!isAnyNegative) {
      return {
        rangemode: 'nonnegative' as const,
      };
    }

    return {};
  };

  // Plotly recomputes "auto" gridlines on Plotly.react and drops the value-axis helper lines when a
  // trace is toggled to visible:false (e.g. hiding a legend item). Pin showgrid explicitly per axis:
  // on for the numeric value axis, off for the category axis, so the value-axis helper lines survive
  // legend toggling while the category axis never gains stray gridlines. When every trace is hidden
  // there is nothing to plot, so suppress gridlines on both axes — the chart stays completely blank
  // instead of showing the default gridlines Plotly draws over its empty linear-axis fallback.
  // Explicit _layout overrides still win.
  const isTraceVisible = (trace: LightChartTrace) => {
    const {visible} = trace as {visible?: unknown};
    return visible !== false && visible !== 'legendonly';
  };
  const axisHasNumericData = (axisKey: 'x' | 'y') =>
    data.some((trace) => {
      const values = (trace as Record<string, unknown>)[axisKey];
      return Array.isArray(values) && values.some((v) => typeof v === 'number');
    });
  const anyTraceVisible = data.some(isTraceVisible);
  const xShowGrid = anyTraceVisible && axisHasNumericData('x');
  const yShowGrid = anyTraceVisible && axisHasNumericData('y');
  const xGridDefault = {showgrid: xShowGrid};
  const yGridDefault = {showgrid: yShowGrid};

  const layout: LightChartLayout = useMemo(
    () => ({
      ..._layout,
      barmode: _layout.barmode ?? 'stack',
      yaxis: {
        ...getDefaultRange(),
        ...yGridDefault,
        ..._layout.yaxis,
      },
      xaxis: {
        ...getDefaultRange(),
        ...xGridDefault,
        ..._layout.xaxis,
      },
      margin: {
        ..._layout.margin,
        t: (_layout.margin?.t ?? 0) + TOOLBAR_MARGIN_TOP,
        l: _layout.margin?.l ?? 0,
        b: _layout.margin?.b ?? 0,
        r: _layout.margin?.r ?? 0,
        pad: _layout.margin?.pad ?? 5,
      },
      height: floorPlotSize(_layout.height ?? wrapperSize.height),
      width: floorPlotSize(wrapperSize.width),
      autosize: true,
      separators, // format: `${decimalSeparator}${thousandsSeparator}`
    }),
    [_layout, wrapperSize.width, wrapperSize.height, isAnyDataVisible, xShowGrid, yShowGrid] // eslint-disable-line
  );
  // download
  const handleDownload = useCallback(
    (chartTag: Plotly.PlotlyHTMLElement, _event: MouseEvent) => {
      // copy chart layout
      const image: any = chartTag.cloneNode(true);
      Object.assign(image, chartTag);
      image.layout = {
        ...image.layout,
        margin: {...layout.margin},
        width: (chartTag as any)._fullLayout.width,
        height: (chartTag as any)._fullLayout.height,
      };
      // default margin
      const IMAGE_MARGIN = {t: 10, l: 5, b: 5, r: 5};
      const TITLE_MARGIN_BOTTOM = 10;
      image.layout.margin.l += IMAGE_MARGIN.l;
      image.layout.margin.r += IMAGE_MARGIN.r;
      image.layout.width += IMAGE_MARGIN.l + IMAGE_MARGIN.r;
      image.layout.margin.b += IMAGE_MARGIN.b;
      image.layout.margin.t = Math.max(image.layout.margin.t - TOOLBAR_MARGIN_TOP, 0) + IMAGE_MARGIN.t;
      image.layout.height += IMAGE_MARGIN.b;
      image.layout.height += image.layout.margin.t - (chartTag as any).layout.margin.t;
      // image title
      const titleFontSize = imageDownload?.title?.font?.size ?? 14;
      const titleFontSizeRatio = titleFontSize / 14;
      const titleText = imageDownload?.title?.text || image.layout.title?.text || 'Untitled chart';
      // Wrap the title to the image width so a long title (patient name + department + date range)
      // doesn't overflow and get cropped on both sides on a narrow chart. ~0.6em average Arial
      // glyph width keeps the estimate conservative (errs towards wrapping rather than clipping).
      const titleMaxChars = Math.floor(image.layout.width / (titleFontSize * 0.6));
      const wrappedTitleText = wrapTextByChars(titleText, titleMaxChars);
      const titleLineCount = wrappedTitleText.split('<br>').length;
      const titleExtraLineCount = Math.max(titleLineCount, 1) - 1;
      const linearFunction = (x: number, a: number, b: number) => a * x + b;
      const titlePaddingTop = linearFunction(titleExtraLineCount, 10 * titleFontSizeRatio, 1) + IMAGE_MARGIN.t;
      const titleHeight = linearFunction(titleExtraLineCount, 18, TITLE_MARGIN_BOTTOM + 14) * titleFontSizeRatio;
      const hasDownloadTitle = Boolean(imageDownload?.title ?? image.layout.title);
      const imageDownloadTitle = hasDownloadTitle
        ? {
            text: wrappedTitleText,
            font: {
              family: imageDownload?.title?.font?.family ?? image.layout.title?.font?.family ?? 'Arial',
              size: titleFontSize,
              color: imageDownload?.title?.font?.color ?? image.layout.title?.font?.color ?? '#4f5e6e', // default value is var(--dlFont)
            },
            y: 1,
            yanchor: 'top',
            pad: {t: titlePaddingTop},
          }
        : null;
      if (imageDownloadTitle) {
        image.layout.margin.t += titleHeight;
        image.layout.height += titleHeight;
        image.layout.title = imageDownloadTitle;
      }
      // image legend
      image.layout.showlegend = imageDownload?.showlegend ?? image.layout.showlegend;
      image.layout.legend = {
        ...(image.layout.legend ?? {}),
        traceorder: 'normal',
      };
      // wrap long legend labels onto multiple lines (instead of truncating them with an ellipsis)
      // so they stay fully readable without consuming the chart area on a single line
      const LEGEND_MAX_CHARS = 25;
      image.data = (image.data as any[]).map((trace: any) => ({...trace, name: wrapTextByChars(trace.name, LEGEND_MAX_CHARS)}));
      // download
      Plotly.downloadImage(image, {
        filename: imageDownload?.fileName ?? 'newplot',
        width: image.layout.width,
        height: image.layout.height,
        format: 'png',
      });
    },
    [imageDownload, layout]
  );
  const config: LightChartConfig = {
    ..._config,
    displaylogo: _config.displaylogo ?? false,
    showTips: _config.showTips ?? false,
    modeBarButtonsToRemove: [
      ...(_config.modeBarButtonsToRemove ?? []),
      'toImage', // remove original button for downloading image
      'sendDataToCloud', // remove random button
      'lasso2d',
      'select2d',
      'autoScale2d',
    ] as Plotly.ModeBarDefaultButtons[],
    modeBarButtonsToAdd: [
      ...(_config.modeBarButtonsToAdd ?? []),
      {
        // add custom button for downloading image
        name: 'toImageCustom',
        title: t('chart.downloadPlot'),
        icon: Plotly.Icons.camera,
        click: handleDownload,
      },
    ],
    showAxisRangeEntryBoxes: _config.showAxisRangeEntryBoxes ?? false,
  };
  // onClick
  const prevOnAfterPlot = useRef(null as ((event: MouseEvent) => void) | null);
  const onAfterPlotCallback = (event: MouseEvent) => {
    const yTickData = (event.target as HTMLElement).getAttribute('data-unformatted') ?? '';
    if (onClickYTick) onClickYTick(yTickData);
  };
  // resize
  const chartRef = useRef(null as any);
  const prevWrapperSize = useRef(wrapperSize);
  const _onResize = useCallback(() => {
    const wrapperSize = getWrapperSize();
    const gotNewWidth = wrapperSize.width !== prevWrapperSize.current.width;
    const gotNewHeight = wrapperSize.height !== prevWrapperSize.current.height;
    if (gotNewWidth) {
      prevWrapperSize.current.width = wrapperSize.width;
    }
    if (gotNewHeight) {
      prevWrapperSize.current.height = wrapperSize.height;
    }
    // Re-render so the explicit, floored width/height in `layout` follow the wrapper; Plotly.react
    // then resizes the plot. We deliberately do NOT call Plotly.Plots.resize: it reads the raw
    // container size and would bypass the MIN_PLOT_PX floor, re-introducing the axis-scaling crash.
    if (gotNewWidth || gotNewHeight) {
      rerender();
    }
  }, [getWrapperSize, rerender]);
  const onResizeDebounced = useDebounce(_onResize, 0);
  const resizeObserver = useRef(null as ResizeObserver | null);
  const {setRef: chartWrapperSetRef} = useOnMount({
    onMount: (node) => {
      chartWrapperRefValue.current = node;
      resizeObserver.current = new ResizeObserver(onResizeDebounced);
      resizeObserver.current.observe(node);
      onResizeDebounced();
    },
    onUnMount: (_prevNode) => {
      if (resizeObserver.current) resizeObserver.current.disconnect();
      chartWrapperRefValue.current = null;
    },
  });
  const handleRelayout = () => {
    const currentChartData = chartRef.current?.props.data;
    setIsAnyDataVisible(Array.isArray(currentChartData) ? currentChartData.length > 0 : false);
  };
  // display
  const theme = useTheme();
  const originalDataIsEmpty = originalDataLength == null || originalDataLength === 0;
  const isNotEmpty =
    (originalDataLength != null ? originalDataLength > 0 : false) ||
    (data && data.length > 0 && data.some((v) => v?.x?.length && v?.y?.length));
  const muiStyles = getStyles({theme, params: {dlChartStyles: chartStyles, originalDataIsEmpty}});
  if (isLoading) {
    return <LoadingSpinner isVisible marginTop={0} wrapperStyles={{flexGrow: 1, alignContent: 'center'}} />;
  }
  return (
    <div {...restProps} ref={chartWrapperSetRef} css={muiStyles.dlChart}>
      {isNotEmpty ? (
        <>
          <Plot
            {...{ref: chartRef}}
            data={cloneDeep(data)} // NOTE: Plot component wrongly modifies its props, so we need to create duplicate here
            layout={cloneDeep(layout)} // NOTE: Plot component wrongly modifies its props, so we need to create duplicate here
            config={cloneDeep(config)} // NOTE: Plot component wrongly modifies its props, so we need to create duplicate here
            style={plotStyle}
            className={onClickYTick ? ' hasOnClickYTick' : ''}
            onClick={onClick}
            onHover={onHover}
            onUnhover={onUnhover}
            onAfterPlot={() => {
              const plotTag = chartRef?.current?.el;
              if (!plotTag) return;
              // a redraw recreates the tick nodes (dropping their listeners), so reset the tooltip
              yTickTooltipRef.current?.hide();
              // map each rendered y category value to its full (un-truncated) label so we can show a
              // tooltip on truncated y-axis tick labels (Plotly has no built-in tooltip for axis ticks)
              const yTickTooltipMap = new Map<string, string>();
              data.forEach((trace) => {
                const {y, customdata} = trace as {y?: (string | number)[]; customdata?: (string | number)[]};
                if (Array.isArray(y) && Array.isArray(customdata)) {
                  y.forEach((yValue, index) => {
                    const fullLabel = customdata[index];
                    if (fullLabel != null) yTickTooltipMap.set(String(yValue), String(fullLabel));
                  });
                }
              });
              const yTickTags = [...plotTag.querySelectorAll('.yaxislayer-above text')];
              yTickTags.forEach((yTick) => {
                yTick.removeEventListener('click', prevOnAfterPlot.current);
                yTick.addEventListener('click', onAfterPlotCallback);
                // show the full label in the (table-style) tooltip on every y-axis tick label
                const fullLabel = yTickTooltipMap.get(yTick.getAttribute('data-unformatted') ?? '');
                yTick.removeEventListener('mouseover', handleYTickMouseOver);
                yTick.removeEventListener('mouseout', handleYTickMouseOut);
                const yTickStyle = (yTick as SVGElement).style;
                if (fullLabel) {
                  yTick.setAttribute('data-yticktooltip', fullLabel);
                  yTick.addEventListener('mouseover', handleYTickMouseOver);
                  yTick.addEventListener('mouseout', handleYTickMouseOut);
                  // Plotly sets pointer-events:none on tick labels, so real hovering never reaches
                  // them; re-enable it so the tooltip can trigger
                  yTickStyle.pointerEvents = 'all';
                  yTickStyle.cursor = 'default';
                } else {
                  yTick.removeAttribute('data-yticktooltip');
                  yTickStyle.pointerEvents = '';
                  yTickStyle.cursor = '';
                }
              });
              prevOnAfterPlot.current = onAfterPlotCallback;
            }}
            onRelayout={handleRelayout}
          />
          <YAxisTooltip ref={yTickTooltipRef} />
        </>
      ) : (
        <div
          css={muiStyles.dlChartEmptyWrapper}
          style={{height: '100%', ...(originalDataIsEmpty ? {overflow: 'visible'} : {}), ...(emptyWrapperStyle ?? {})}}
        >
          {noDataComponent || <div css={muiStyles.dlChartEmptyData}>{emptyMessage || t('chart.emptyData')}</div>}
        </div>
      )}
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => {
  return {
    dlChart: css({
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      overflow: options?.params?.originalDataIsEmpty ? 'visible' : 'hidden',
      ...(options?.params?.originalDataIsEmpty ? {'& svg': {overflow: 'visible !important'}} : {}),
      ...(options?.params?.dlChartStyles || {}),
    }),
    dlChartEmptyWrapper: css`
      display: flex;
      justify-content: center;
    `,
    dlChartEmptyData: css`
      margin: auto;
      width: 100%;
      text-align: center;
    `,
  };
};

// presets
export function useChartDateStops() {
  const intl = useIntl();
  return {
    tickformat: DateUtils.getLocaleD3FormatPattern(intl.locale),
    tickformatstops: [
      {
        dtickrange: [null, 1000],
        value: '%H:%M:%S \n %d. %m.',
      },
      {
        dtickrange: [1000, 60000],
        value: '%H:%M:%S \n %d. %m.',
      },
      {
        dtickrange: [60000, 3600000],
        value: '%H:%M \n %d. %m.',
      },
      {
        dtickrange: [3600000, 82800000],
        value: '%H:%M \n %d. %m.',
      },
      {
        dtickrange: [82800000, 86400000],
        value: '%d. %m. \n %y',
      },
      {
        dtickrange: [86400000, 604800000],
        value: '%d. %m. \n %y',
      },
      {
        dtickrange: [604800000, 'M1'],
        value: '%d. %m. \n %y',
      },
      {
        dtickrange: ['M1', 'M12'],
        value: "%m '%y",
      },
      {
        dtickrange: ['M12', null],
        value: '%Y',
      },
    ],
  };
}
// https://d3js.org/d3-format
// https://github.com/d3/d3-time-format/blob/master/README.md
export const CHART_MONTH_TICKFORMAT = DateUtils.getLocaleMonthYearPattern();
export function useChartDateTimeStops(): LayoutAxis {
  const intl = useIntl();
  return {
    tickformat: DateUtils.getLocaleD3TimeFormatPattern(intl.locale),
    tickformatstops: [
      {
        dtickrange: [null, 1000],
        value: intl.locale === 'en' ? '%I:%M:%S %p \n %m/%d' : '%H:%M:%S \n %d. %m.',
      },
      {
        dtickrange: [1000, 60000],
        value: intl.locale === 'en' ? '%I:%M:%S %p \n %m/%d' : '%H:%M:%S \n %d. %m.',
      },
      {
        dtickrange: [60000, 3600000],
        value: intl.locale === 'en' ? '%I:%M %p \n %m/%d' : '%H:%M \n %d. %m.',
      },
      {
        dtickrange: [3600000, 82800000],
        value: intl.locale === 'en' ? '%I:%M %p \n %m/%d' : '%H:%M \n %d. %m.',
      },
      {
        dtickrange: [82800000, 86400000],
        value: intl.locale === 'en' ? '%m/%d \n /%y' : '%d. %m. \n %y',
      },
      {
        dtickrange: [86400000, 604800000],
        value: intl.locale === 'en' ? '%m/%d \n /%y' : '%d. %m. \n %y',
      },
      {
        dtickrange: [604800000, 'M1'],
        value: intl.locale === 'en' ? '%m/%d \n /%y' : '%d. %m. \n %y',
      },
      {
        dtickrange: ['M1', 'M12'],
        value: "%m '%y",
      },
      {
        dtickrange: ['M12', null],
        value: '%Y',
      },
    ],
  };
}
